import { useContext, useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import { isEmpty } from 'lodash';
import queryString from 'query-string';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { notFoundPath } from 'common/config/urls';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import LayoutContext from 'context/LayoutContext';
import {
  CURRENT_VALUATIONS_SLUG,
  INFORMATION_SLUG,
  MODELS_SLUG,
  OPPORTUNITIES_SLUG,
  OVERVIEW_SLUG,
  PAST_VALUATIONS_SLUG,
  PREVIOUS_REPORTS_SLUG,
  SETTINGS_SLUG,
  SUBSCRIPTION_SLUG,
} from 'dashboard409a/common/constants/urls';
import { Overview, Settings, Subscription } from 'dashboard409a/pages';
import { getCompanyDetails } from 'dashboard409a/states/features/company';
import { updateGlobalState } from 'dashboard409a/states/features/global';
import { findValuationScalarOpinion, getValuationScalarOpinion } from 'dashboard409a/states/features/valuation';
import { AppDispatchType, RootStateType } from 'dashboard409a/states/store';
import InformationData from '../Information/InformationData';
import { WorkboardDataTable } from '../Workboard/WorkboardDataTable';
import WorkboardModels from '../Workboard/WorkboardModels/WorkboardModels';

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { companySlug, tableSlugParam, tabSlugParam } = useParams();
  const companies = useSelector((state: RootStateType) => state.user.standalone_companies_permissions);
  const dispatch = useDispatch<AppDispatchType>();
  const { currentCompanyMeasurementDateId } = useSelector((state: RootStateType) => state.global);

  const [{ user }] = useStore() as unknown as UseStoreValues;

  // Determine if we're in Workboard mode (no companySlug, but tabSlugParam exists)
  const isWorkboard = !companySlug && tabSlugParam;
  const currentSlug = isWorkboard ? tabSlugParam : tableSlugParam;

  const { setPageTitle, setNavItems, setShowAlert, setAlertMessage } = useContext(LayoutContext);

  const currentCompany = useMemo(
    () => companies?.find(company => company.slug === companySlug) || null,
    [companies, companySlug]
  );

  const userIsStaff = useMemo(() => {
    if (user) {
      return user.is_staff;
    }
    return false;
  }, [user]);

  const parsedQuery = useMemo(() => queryString.parse(location.search), [location]);

  const menuItems = useMemo(() => {
    const navigateToUrl = (slug: string) => {
      if (isWorkboard) {
        const newPathName = `/dashboard/analyst/${slug}`;
        navigate({ pathname: newPathName, search: queryString.stringify(parsedQuery) });
      } else if (companySlug) {
        const newPathName = `/dashboard/companies/${companySlug}/${slug}`;
        navigate({ pathname: newPathName, search: queryString.stringify(parsedQuery) });
      }
    };

    if (isWorkboard) {
      // Workboard menu items
      return [
        {
          callback: () => navigateToUrl(CURRENT_VALUATIONS_SLUG),
          id: 'current-valuations',
          itemKey: 0,
          slug: CURRENT_VALUATIONS_SLUG,
          title: 'Current Valuations',
        },
        {
          callback: () => navigateToUrl(PAST_VALUATIONS_SLUG),
          id: 'past-valuations',
          itemKey: 1,
          slug: PAST_VALUATIONS_SLUG,
          title: 'Past Valuations',
        },
        {
          callback: () => navigateToUrl(OPPORTUNITIES_SLUG),
          id: 'opportunities',
          itemKey: 2,
          slug: OPPORTUNITIES_SLUG,
          title: 'Opportunities',
        },
        {
          callback: () => navigateToUrl(MODELS_SLUG),
          id: 'models',
          itemKey: 3,
          slug: MODELS_SLUG,
          title: 'Models',
        },
      ];
    }

    // Company dashboard menu items
    return [
      {
        callback: () => navigateToUrl(OVERVIEW_SLUG),
        id: 'overview',
        itemKey: 0,
        slug: OVERVIEW_SLUG,
        title: 'Overview',
      },
      {
        callback: () => navigateToUrl(PREVIOUS_REPORTS_SLUG),
        id: 'previous-reports',
        itemKey: 1,
        slug: PREVIOUS_REPORTS_SLUG,
        title: 'Previous Reports',
      },
      ...(userIsStaff
        ? [
          {
            callback: () => navigateToUrl(INFORMATION_SLUG),
            id: 'information',
            itemKey: 2,
            slug: INFORMATION_SLUG,
            title: 'Information',
          },
          {
            callback: () => navigateToUrl(SETTINGS_SLUG),
            id: 'settings',
            itemKey: 3,
            slug: SETTINGS_SLUG,
            title: 'Settings',
          },
          {
            callback: () => navigateToUrl(SUBSCRIPTION_SLUG),
            id: 'subscription',
            itemKey: 4,
            slug: SUBSCRIPTION_SLUG,
            title: 'Subscription',
          },
        ]
        : []),
    ];
  }, [isWorkboard, companySlug, userIsStaff, parsedQuery, navigate]);

  // Hide alert box when browsing to another location
  useEffect(
    () => () => {
      setShowAlert?.(false);
      setAlertMessage?.('');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname]
  );

  useEffect(() => {
    if (currentCompany?.id) {
      dispatch(updateGlobalState({ currentCompany }));
      dispatch(getValuationScalarOpinion(currentCompany?.id));
      dispatch(getCompanyDetails(currentCompany?.id));
    }

    if (currentCompanyMeasurementDateId) {
      dispatch(findValuationScalarOpinion(currentCompanyMeasurementDateId));
    }
  }, [currentCompany, currentCompanyMeasurementDateId, dispatch]);

  useEffect(() => {
    if (!isEmpty(currentCompany) && currentCompany.name) {
      setPageTitle?.(currentCompany.name);
      setNavItems?.(menuItems);
    }

    return () => {
      setPageTitle?.(null);
      setNavItems?.(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany, location, menuItems]);

  // Handle company dashboard navigation redirects (only for invalid slugs)
  useEffect(() => {
    const handleCompanyDashboardNavigation = () => {
      const validCompanySlugs = [
        OVERVIEW_SLUG,
        PREVIOUS_REPORTS_SLUG,
        INFORMATION_SLUG,
        SETTINGS_SLUG,
        SUBSCRIPTION_SLUG,
      ];

      if (currentSlug && !validCompanySlugs.includes(currentSlug)) {
        navigate(`/dashboard/companies/${companySlug}/${OVERVIEW_SLUG}`, { replace: true });
      }
    };

    if (!isWorkboard) {
      if (!companySlug) {
        navigate(notFoundPath, { replace: true });
      } else {
        handleCompanyDashboardNavigation();
      }
    }
  }, [isWorkboard, companySlug, currentSlug, navigate]);

  const renderCurrentView = () => {
    switch (currentSlug) {
      // Company dashboard views
      case OVERVIEW_SLUG:
      case PREVIOUS_REPORTS_SLUG:
        return <Overview />;
      case INFORMATION_SLUG:
        return userIsStaff ? <InformationData /> : <></>;
      case SETTINGS_SLUG:
        return userIsStaff ? <Settings /> : <></>;
      case SUBSCRIPTION_SLUG:
        return userIsStaff ? <Subscription /> : <></>;
      case CURRENT_VALUATIONS_SLUG:
      case PAST_VALUATIONS_SLUG:
      case OPPORTUNITIES_SLUG:
        return userIsStaff || user?.is_superuser ? <WorkboardDataTable /> : <></>;
      case MODELS_SLUG:
        return userIsStaff || user?.is_superuser ? <WorkboardModels /> : <></>;

      default:
        return <></>;
    }
  };

  // For Workboard, we don't need companySlug
  if (!isWorkboard && !companySlug) {
    return null;
  }

  // Block unauthorized users from accessing workboard once user data is loaded
  if (isWorkboard && user && !user.is_staff && !user.is_superuser) {
    return <Navigate to={notFoundPath} replace />;
  }

  return (
    <Box
      sx={{
        width: '100%',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}>
      {renderCurrentView()}
    </Box>
  );
};

export default Dashboard;
